import projImg1 from "../assets/img/Industry01.jpg";
import projImg2 from "../assets/img/Industry03.jpg";
import projImg3 from "../assets/img/Industry04.jpg";
import projImg4 from "../assets/img/Industry05.jpg";
import projImg5 from "../assets/img/Industry06.jpg";
import projImg6 from "../assets/img/Industry07.jpg";
import projImg7 from "../assets/img/Industry02.jpg";

import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ArrowRightCircle } from "react-bootstrap-icons";

const industries = [
  {
    id: "Aerial",
    number: "01",
    category: "Space & Aerial Imaging",
    title: "Aerial and Satellites",
    image: projImg3,
    alt: "Satellite imaging application",
    text: "From crop-health surveys to post-disaster assessment, airborne and orbital cameras give decision-makers an instant, map-level view of the world. When images are stitched and analysed in the cloud—or directly on the aircraft—they reveal subtle changes in vegetation, infrastructure or ice cover that the human eye would miss. This keeps inspection crews off dangerous terrain, speeds up insurance claims after storms, and helps planners manage resources more sustainably. Kristal Fotonik’s lightweight, radiometrically-calibrated cameras can drop straight into standard gimbals, giving drone and small-sat builders an immediate upgrade path for these aerial analytics missions.",
  },
  {
    id: "Automation",
    number: "02",
    category: "Industrial Vision",
    title: "Automation",
    image: projImg2,
    alt: "Industrial automation and robotic production line",
    text: "Vision systems turn ordinary production lines into self-optimising cells. Cameras spot surface defects, confirm assembly order, read barcodes and measure dimensions while parts are still in motion. The feedback loop lets controllers correct a process before scrap is produced, driving down re-work and material waste. Because each inspection step is recorded, plants also gain a digital audit trail that simplifies quality-control reporting. The industrial cameras Kristal Fotonik builds integrate edge inference, so manufacturers can detect scratches or alignment errors on the line and pass decisions directly to their PLC without extra hardware.",
  },
  {
    id: "Healthcare",
    number: "03",
    category: "Medical Imaging",
    title: "Healthcare and Medical",
    image: projImg5,
    alt: "Medical imaging application",
    text: "Imaging is central to modern diagnostics and therapy: digital microscopes automatically count cells, surgical cameras provide the live view that makes minimally-invasive procedures possible, and software can mark tumours or arterial plaques a human eye might overlook. By turning pictures into consistent, quantifiable data, clinicians reach a diagnosis sooner and choose the best treatment with confidence. Kristal Fotonik’s sterilizable micro-camera heads deliver vivid, true-to-colour visuals with virtually no delay, helping surgeons work more efficiently and supporting a quicker recovery path for patients.",
  },
  {
    id: "MachineV",
    number: "04",
    category: "Quality Control",
    title: "Machine Vision",
    image: projImg1,
    alt: "Machine vision inspection system",
    text: "In logistics, food, pharma and electronics, vision systems verify labels, track parcels, align printed circuits and flag packaging defects while everything keeps moving. When a model sees an out-of-tolerance part, the line can divert it before the next station, slashing scrap and re-work. Kristal Fotonik’s robust, global-shutter cameras slot straight into these inspection cells and deliver distortion-free images that machine-learning models can trust, even with reflective metals or uneven lighting.",
  },
  {
    id: "Robotic",
    number: "05",
    category: "Autonomous Systems",
    title: "Robotics",
    image: projImg4,
    alt: "Robotic vision application",
    text: "Robots rely on vision to understand and interact with dynamic environments. Grippers locate irregular parts on a moving belt, mobile robots build their own maps as they navigate warehouses, and collaborative arms monitor human co-workers so they can slow or stop when a person steps too close. Vision-based feedback lets the same robot switch seamlessly between tasks—picking, placing, sorting—without mechanical re-tooling, making automation flexible rather than rigid. Thanks to their compact housings and fast onboard processing, Kristal Fotonik’s vision modules mount easily on end effectors and provide the positional cues needed for agile, high-mix automation.",
  },
  {
    id: "ScientificR",
    number: "06",
    category: "Scientific Imaging",
    title: "Scientific Research",
    image: projImg7,
    alt: "Scientific research imaging application",
    text: "Researchers rely on imaging to capture once-only events—from neuron firing patterns to crystallisation fronts or meteor streaks. Live streams feed analysis software so scientists can tweak an experiment while it’s still running, turning “record-then-process” into real-time discovery. Thanks to their low-noise sensors and stable response, Kristal Fotonik’s cameras preserve weak signals without masking them in electronic grain, making them a solid fit for microscopes, spectroscopy benches and small-telescope rigs.",
  },
  {
    id: "SmartC",
    number: "07",
    category: "Urban Vision",
    title: "Smart Cities",
    image: projImg6,
    alt: "Smart city imaging and traffic monitoring",
    text: "Vision platforms now watch intersections, tunnels and public spaces, translating raw video into traffic counts, hazard alerts and crowd-flow data. This lets operators retime lights before congestion builds, dispatch crews when a road surface cracks, or trigger alarms at the first hint of smoke. Built for continuous outdoor duty and capable of on-device analytics, Kristal Fotonik’s cameras can slot onto these poles and cabinets to supply the reliable, real-time imagery that intelligent-transport and safety systems depend on.",
  },
];

const IndustriesItems = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace("#", "");

    if (!hash) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      const element = document.getElementById(hash);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);

    return () => window.clearTimeout(scrollTimer);
  }, [location]);

  return (
    <main className="industries-page">
      <section className="industries-hero">
        <div className="industries-hero__glow industries-hero__glow--one" />
        <div className="industries-hero__glow industries-hero__glow--two" />

        <div className="industries-container industries-hero__content">
          <span className="industries-eyebrow">
            Imaging Technologies in Action
          </span>

          <h1>
            Industries We
            <span> Enable</span>
          </h1>

          <p>
            From orbit to the production line, Kristal Fotonik develops imaging
            technologies that help machines observe, understand and respond to
            the world with greater accuracy.
          </p>

          <div className="industries-hero__stats">
            <div>
              <strong>07</strong>
              <span>Industry Areas</span>
            </div>

            <div>
              <strong>Real-Time</strong>
              <span>Image Processing</span>
            </div>

            <div>
              <strong>Edge-Ready</strong>
              <span>Vision Solutions</span>
            </div>
          </div>
        </div>
      </section>

      <nav className="industries-index" aria-label="Industry sections">
        <div className="industries-container industries-index__inner">
          {industries.map((industry) => (
            <a key={industry.id} href={`#${industry.id}`}>
              <span>{industry.number}</span>
              {industry.title}
            </a>
          ))}
        </div>
      </nav>

      <section className="industries-list">
        <div className="industries-container">
          {industries.map((industry, index) => (
            <article
              id={industry.id}
              key={industry.id}
              className={`industry-panel ${
                index % 2 !== 0 ? "industry-panel--reverse" : ""
              }`}
            >
              <div className="industry-panel__visual">
                <div className="industry-panel__number">
                  {industry.number}
                </div>

                <img src={industry.image} alt={industry.alt} loading="lazy" />

                <div className="industry-panel__image-line" />
              </div>

              <div className="industry-panel__content">
                <span className="industry-panel__category">
                  {industry.category}
                </span>

                <h2>{industry.title}</h2>

                <div className="industry-panel__divider" />

                <p>{industry.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="industries-cta">
        <div className="industries-container">
          <div className="industries-cta__box">
            <div>
              <span className="industries-eyebrow">
                Build Your Vision System
              </span>

              <h2>Have an imaging challenge in mind?</h2>

              <p>
                Explore our imaging products or contact our team to discuss the
                requirements of your application.
              </p>
            </div>

            <div className="industries-cta__actions">
              <Link to="/products" className="industries-button industries-button--light">
                Explore Products
                <ArrowRightCircle />
              </Link>

              <HashLink
                smooth
                to="/#connect"
                className="industries-button industries-button--outline"
              >
                Contact Our Team
              </HashLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IndustriesItems;